import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/never';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/concat';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/bufferCount';
import 'rxjs/add/operator/concat';
import 'rxjs/add/operator/delay';
import 'observables/ObservableExtensions';
import includes from 'lodash/includes';
import get from 'lodash/get';
import {
    createdImageAction,
    createImageAction,
    deletedImageAction,
    endEditImageAction,
    endGenerateImagesAction,
    generateImagesAction,
    pickedOriginalAction,
    pickOriginalAction,
    receivedSiblingImages,
    removedImageFromCurrentBreakAction,
    savedImageDataAction,
    setExistingImageAction,
    setUploadingImagesFor,
    startEditImageAction,
    startGenerateImagesAction,
    Types,
    updatePendingImageUploadAction,
    uploadedOriginalAction,
    uploadOriginalAction,
    uploadSuccessAction
} from "./actions";
import * as ResourceApi from 'api/resources';
import * as ResourceActions from 'resources/actions';
import {clone, isNoU} from "utils/DBUtils";
import {filterOptionsFactory, sortOptionsFactory} from "viewOptions/factories";
import {createResourcePageSelector} from 'resources/selectors';
import {fillImageDataFromFile, TempImageType} from "./utils";
import ImageDimensions from "utils/ImageDimensions";
import {GIFType} from "../../utils/ImageDimensions";
import {makeDBOriginalImageUrl} from "../../utils/DBUtils";
import {RetailerItemImageType} from 'pages/Rewards/utils';
import {fromSources} from 'observables/lettables/pickFile';

const imagesWithoutSources = [
  RetailerItemImageType
];

const pickOriginalEpic = (action$) => action$.ofType(Types.PICK_ORIGINAL)
    .mergeMap((action) => Observable.DB.pickFile(action.optionOverrides)
        .mergeMap((file) => Observable.DB.imageMetadata(file, {width: true, height: true})
            .mergeMap((metadata) => {
                if (includes(fromSources, file.source)) {
                    delete file.source;
                }

                const pendingOriginalUpload = {...file, ...metadata};
                const nextActions = [pickedOriginalAction(pendingOriginalUpload)];

                // the case of uploading a new image to an existing RetailerItem
                const editingRetailerItem = get(action.forResourceId, 'type', null) === ResourceApi.Types.StoreCatalog;

                if (!action.forResourceId || editingRetailerItem) {
                    const {key, height, width} = pendingOriginalUpload;
                    const selectedImage = fillImageDataFromFile({
                        id: key,
                        imageType: TempImageType,
                        height,
                        width
                    }, pendingOriginalUpload);

                    nextActions.push(uploadOriginalAction(selectedImage));
                }

                return Observable.of(...nextActions);
            })
            .catch(error => Observable.of({type: "ERROR", error: error}))
        )
    );

const getResourcePage = createResourcePageSelector(ResourceApi.Types.Image);

const uploadOriginalEpic = (action$, store) => action$.ofType(Types.UPLOAD_ORIGINAL)
    .mergeMap((action) => {
        let imagesState = store.getState().images;
        let parentIds = [];

        if (!isNoU(imagesState.pickingOriginalForResourceId)) {
            parentIds = [imagesState.pickingOriginalForResourceId];
        }

        // If we are creating an image after uploading an original,
        // associate original with same resources as the image to be created
        if (!isNoU(imagesState.createAfterOriginal)) {
            parentIds = imagesState.createAfterOriginal.parentIds || [];
        } else if (!isNoU(imagesState.generateAfterOriginal)) {
            parentIds = [{type: ResourceApi.Types.Break, id: imagesState.generateAfterOriginal.breakId}];
        }

        const imageToUpload = imagesState.selectedPendingImage;

        return ResourceApi.create(
            ResourceApi.Types.OriginalImage, {
                name: imageToUpload.key,
                // clone over data input by user and fill in defaults
                info: fillImageDataFromFile({
                    ...imagesState.originalImageData,
                    name: imageToUpload.name,
                    createdFor: imagesState.pickingOriginalForResourceId
                }, imageToUpload),
            }, parentIds)
            .mergeMap(({response: {object}}) => {
                // Will always output these actions
                let actions = [
                    ResourceActions.createdResourceAction(object, {}, [], [], []),
                ];

                const forResourceType = get(imagesState.pickingOriginalForResourceId, 'type', null);
                const isRetailerItem = forResourceType === ResourceApi.Types.RetailerItem;

                // if (!isRetailerItem) {
                    // dispatch for every resource type except RetailerItems
                    actions.push(uploadSuccessAction(object));

                    // don't wait for `uploaded` flag to be set in this case; this way we
                    // can close the edit modal immediately after cropping.
                    if (imagesState.pendingImages.length === 1) {
                        actions.push(uploadedOriginalAction(object));
                    }
                // }

                if (imagesState.pendingImages.length === 1 && imagesState.pendingImages[0].uploaded === true && actions.length < 3) {
                    // if uploading in bulk, this action will be dispatched from a container instead of from here.
                    actions.push(uploadedOriginalAction(object));
                } else if (!imagesState.uploadingImagesFor) {
                    // for bulk uploads we want to know the break resource id for which they are being uploaded.
                    actions.push(setUploadingImagesFor(object.createdFor));
                }

                // Only output additional action if their is an image to create after upload
                if (!isNoU(imagesState.createAfterOriginal)) {
                    actions.push(createImageAction(
                        object.id,
                        imagesState.createAfterOriginal.imageType,
                        imagesState.createAfterOriginal.rootResource,
                        imagesState.createAfterOriginal.forResourceId,
                        imagesState.createAfterOriginal.resourcePath,
                        imagesState.createAfterOriginal.fieldPath,
                        imagesState.createAfterOriginal.field,
                        imagesState.createAfterOriginal.parentIds
                    ));
                } else if (!isNoU(imagesState.generateAfterOriginal)) {
                  actions.push(generateImagesAction(
                    imagesState.generateAfterOriginal.breakId,
                    object.id,
                    imagesState.generateAfterOriginal.types,
                    imagesState.generateAfterOriginal.fields
                  ));
                }

                if (!object.createdFor) {
                    actions.push(startEditImageAction(object.id));

                }
                if (isRetailerItem) {
                    // copy new RetailerItem image's derived name
                    actions.push(updatePendingImageUploadAction('name', object.name));
                }

                return Observable.of(...actions);
            })
            .catch(error => Observable.of({type: "ERROR", error: error}));
    });

// Reducer sets the createAfterOriginal object, so just need to start the epic chain
// Enforce single file upload, since this epic also needs to assign an image id to a field, which means there can only be one image
const uploadOriginalAndCreateImageEpic = (action$, store) => action$.ofType(
  Types.UPLOAD_ORIGINAL_AND_CREATE_IMAGE,
  Types.UPLOAD_ORIGINAL_AND_GENERATE_IMAGES
)
  .map(action => pickOriginalAction(action.forResourceId), {maxFiles: 1});

const generateImagesEpic = (action$, store) => action$.ofType(Types.GENERATE_IMAGES)
/**
 * Will only support Break fields for initial implementation
 */
  .mergeMap(action => {
    let originalImage = store.getState().resources.resources[action.originalImageId];
    if (isNoU(originalImage)) {
      console.error(`no original image loaded with id ${action.originalImageId}`);
    }

    let forResourceId = {type: ResourceApi.Types.Break, id: action.breakId};
    let transforms = [];

    // Create an observable for each image type
    // to create an image from the given original
    for (let type of action.types) {
      let dimensions = ImageDimensions[type];
      let {retina, regular} = dimensions;
      if (isNoU(dimensions)) {
        throw new Error(`Image type ${type} is not in image dimensions map.`);
      }
      transforms.push(
        // Initial transformation
        // Will use as much of the image as possible
        Observable.DB.transformImage(
          { // Mock file object
            url: makeDBOriginalImageUrl(action.originalImageId)
          },
          {
            resize: {
              fit: 'crop',
              width: retina.x,
              height: retina.y,
              align: 'center'
            }
          })
          .mergeMap((retinaFile) => {
            // The retina image is created from the initial transform
            // Now we resize it to regular, then use both to create the image in Beaker API
            console.log("RESIZE", retinaFile);

            return Observable.DB.resizeImage(retinaFile, regular.x, regular.y)
              .mergeMap((regularFile) => ResourceApi.create(ResourceApi.Types.Image, {
                retinaTempImageId: retinaFile.key,
                regularTempImageId: regularFile.key,
                originalImageId: action.originalImageId,
                imageType: type,
                forResourceId: forResourceId
              }, [forResourceId]));
          })
          .catch((err) => Observable.of({type: "ERROR", error: err}))
      );
    }

    // Execute the transformations and image creation in parallel
    return Observable.forkJoin(transforms)
      .mergeMap((responses) => {
          // Responses should be in same order as created
          // so we use the response index to get the field to save
          let i = 0;
          let actions = [];
          let changelist = {};
          for (let response of responses) {
            // An error action is output if the transformation fails
            if (response.type && response.type === "ERROR") {
              actions.push(response);
              continue;
            }

            // object is the created image from Beaker API
            // match its id to the provided field into changelist
            // so we can update the break correctly
            let {object} = response.response;
            changelist[action.fields[i]] = object.id;

            // Accumulate created image and created resource actions
            actions.push(
              createdImageAction(object),
              ResourceActions.createdResourceAction(object, {}, [], [], [])
            );

            i++;
          }

          // Save the change list to the API and then output all accumulated actions
          return ResourceApi.saveChangelist(ResourceApi.Types.Break, action.breakId, changelist, [forResourceId])
            .mergeMap(() => {
              let changeListAction = ResourceActions.saveResourceChangelistSuccess("Break", action.breakId, changelist, action.breakId, []);
              actions.push(changeListAction);
              actions.push(endGenerateImagesAction());
              return Observable.of(...actions);
            })
            .catch((err) => Observable.of({type: "ERROR", error: err}));
        }
      )
      .startWith(startGenerateImagesAction());
  });

const createImageEpic = (action$, store) => action$.ofType(Types.CREATE_IMAGE)
        .mergeMap(action => {
            console.log(`createImageEpic`)
            const state = store.getState();
            const isForRetailerItem = state.rewards.writingRetailerItem;

            let originalImageId = action.originalImageId;

            if (isForRetailerItem) {
                const retailerItemImage = state.resources.resources[state.rewards.writingRetailerItemResource.imageId];

                // if we're editing a retailerItem image, it will have an originalImageId
                // attached. otherwise we're creating a new one, the id of which is the
                // one passed here on the action object.

                if (!isNoU(retailerItemImage)) {
                    // Unless the image was removed before adding a new one
                    originalImageId = (action.forResourceId && get(retailerItemImage, 'originalImageId', null)) || action.originalImageId;
                }
            }

            const originalImage = state.resources.resources[originalImageId];

            if (isNoU(originalImage)) {
                console.error(`no original image loaded with id ${action.originalImageId}`);
            }

            let imageType = action.imageType;

            // Override image type for gifs to disable cropping
            if (originalImage.contentType === 'image/gif') {
                imageType = GIFType;
            }

            if (imageType == GIFType) {
                 return Observable.of({originalImage, imageType})
                .mergeMap((file) => {

                    file.url = makeDBOriginalImageUrl(action.originalImageId)
                    console.log(`GIF branch`, file)

                    return Observable.DB.storeImage(file)
                    .mergeMap((retinaFile) => {
                        return ResourceApi.create(ResourceApi.Types.Image, {
                            retinaTempImageId: retinaFile.key,
                            regularTempImageId: retinaFile.key,
                            originalImageId: action.originalImageId,
                            imageType: imageType,
                            forResourceId: action.forResourceId
                        }, action.parentIds || [])
                        .mergeMap(({response: {object}}) => {
                            console.log("here")
                            let actions = [
                                createdImageAction(object),
                                ResourceActions.createdResourceAction(object, {}, [], [], [])
                            ];
    
                            // if (isForRetailerItem) {
                            //     actions.push(updatePendingImageUploadAction('id', object.id));
                            // }
    
                            // no field to write to if it's an image without a parent resource.
                            if (!includes(imagesWithoutSources, object.imageType)) {
                              actions.unshift(ResourceActions.saveResourceFieldAction(
                                action.forResourceId,
                                action.rootResource,
                                action.resourcePath,
                                action.fieldPath,
                                action.field,
                                object.id,
                                action.parentIds || [],
                                [],
                                []
                              ));
                            }
    
                            return Observable.of(...actions);
                        })    

                    })
                })
            } else {

                return Observable.DB.cropOriginal(originalImage, imageType)
                    .mergeMap((file) => {
                        console.log(`cropOriginal`, file)
                        if (isNoU(file)) {
                            throw new Error('NoImageSelected');
                        }
                        let dimensions = ImageDimensions[imageType];
                        if (isNoU(dimensions)) {
                            throw new Error(`Image type ${imageType} is not in image dimensions map.`);
                        }
                        let {retina, regular} = dimensions;
                        // Solution for GIF's resizing override, will need tweaking if we support dimensions with constraints only on a single axis
                        // Will just create a regular and retina version same as original's dimensions
                        if (isNoU(retina.x) && isNoU(retina.y)
                            && isNoU(regular.x, regular.y)) {
                            regular = {
                                x: originalImage.width,
                                y: originalImage.height
                            };
                            retina = regular;
                        }
                        // Create single observable that will output both retina and regular sized image files to next stage
                        return Observable.concat(
                            Observable.DB.resizeImage(file, retina.x, retina.y),
                            Observable.DB.resizeImage(file, regular.x, regular.y)
                        ).bufferCount(2);
                    }
                )
                .mergeMap(([retinaFile, regularFile]) => ResourceApi.create(ResourceApi.Types.Image, {
                    retinaTempImageId: retinaFile.key,
                    regularTempImageId: regularFile.key,
                    originalImageId: originalImageId,
                    imageType: imageType,
                    forResourceId: action.forResourceId
                }, action.parentIds || [])
                    .mergeMap(({response: {object}}) => {
                            let actions = [
                                createdImageAction(object),
                                ResourceActions.createdResourceAction(object, {}, [], [], [])
                            ];

                            if (isForRetailerItem) {
                                actions.push(updatePendingImageUploadAction('id', object.id));
                            }

                            // no field to write to if it's an image without a parent resource.
                            if (!includes(imagesWithoutSources, object.imageType)) {
                              actions.unshift(ResourceActions.saveResourceFieldAction(
                                action.forResourceId,
                                action.rootResource,
                                action.resourcePath,
                                action.fieldPath,
                                action.field,
                                object.id,
                                action.parentIds || [],
                                [],
                                []
                              ));
                            }

                            return Observable.of(...actions);
                        }
                    ))
                .catch((err) => {
                    let action = {type: "ERROR", error: err};
                    if (err.message === 'NoImageSelected') {
                        // Just end edit if no image selected
                        action = endEditImageAction();
                    }

                    return Observable.of(action);
                });

            }


        }
    );

// Handles the native (non-Filestack) upload modal's onConfirm: uploads either a cropped
// `file` or a `mediaUrl` via the single-call `resource/image/upload` endpoint, then emits
// the same tail of actions createImageEpic emits on success -- both derivative records
// (Image, OriginalImage) land in the resources store, and the new Image id is written
// onto the field via the unchanged saveResourceFieldAction path.
const nativeUploadImageEpic = (action$) => action$.ofType(Types.NATIVE_UPLOAD_IMAGE)
    .mergeMap(action => ResourceApi.uploadNativeImage(
        action.file,
        {
            imageType: action.imageType,
            forResourceId: action.forResourceId,
            mediaUrl: action.mediaUrl,
            preCropped: action.preCropped,
            name: action.name,
            filename: action.filename,
            altAttribute: action.altAttribute,
            source: action.source,
            link: action.link
        },
        action.parentIds || []
    )
        .mergeMap(({response: {object}}) => {
            const {image, originalImage} = object;

            let actions = [
                ResourceActions.createdResourceAction(originalImage, {}, [], [], []),
                ResourceActions.createdResourceAction(image, {}, [], [], []),
                createdImageAction(image)
            ];

            actions.unshift(ResourceActions.saveResourceFieldAction(
                action.forResourceId,
                action.rootResource,
                action.resourcePath,
                action.fieldPath,
                action.field,
                image.id,
                action.parentIds || [],
                [],
                []
            ));

            return Observable.of(...actions);
        })
        .catch(err => Observable.of({type: "ERROR", error: err}))
    );

// Convenience function to create api request that will retrieve all images with a given original image id
const getSiblingImages = (originalImageId) => ResourceApi.getAll(
    ResourceApi.Types.Image,
    sortOptionsFactory(),
    filterOptionsFactory({
        filters: [{
            field: 'originalImageId',
            value: originalImageId
        }]
    }));

const startEditImageEpic = (action$, store) => action$.ofType(Types.START_EDIT_IMAGE)
    .mergeMap(action => {
        const state = store.getState();
        const existingImage = state.resources.resources[action.imageId];

        const setExistingImage = image => {
            if (!(state.images.selectedPendingImage && state.images.pendingImages.length)) {
                // ensure that the reducer has an image to apply edits to. this is a
                // side-effect; we don't want this chained with the sibling images request
                // below since that would cause a perceptible delay for the user.
                store.dispatch(setExistingImageAction(image));
            }
        };

        if (isNoU(existingImage)) {
            // If we don't hav the image locally, get the image and images with same originalImageId
            return ResourceApi.getById(ResourceApi.Types.Image, action.imageId)
                .mergeMap(({response: {object: imageObject}}) => {
                        // If no original image, only output selected image object
                        let originalImageId = imageObject.originalImageId;
                        if (imageObject.imageType === "original") {
                            originalImageId = imageObject.id;
                        }

                        if (isNoU(originalImageId)) {
                            return Observable.of(ResourceActions.receivedResourceAction(ResourceApi.Types.Image, imageObject));
                        }

                        setExistingImage(imageObject);

                        // Else get all images that share this image's original image
                        return getSiblingImages(imageObject.originalImageId)
                            .mergeMap(({response: {object: siblingImages}}) => Observable.of(
                                ResourceActions.receivedAllResourcesAction(ResourceApi.Types.Image, siblingImages),
                                ResourceActions.receivedResourceAction(ResourceApi.Types.Image, imageObject),
                                receivedSiblingImages(siblingImages)
                            ));
                    }
                );
        } else if (existingImage.imageType === "original" || !isNoU(existingImage.originalImageId)) {

            setExistingImage(existingImage);

            // Else use the existing image, but still get images with same originalImageId
            // Don't fetch siblings if original image id doesn't exist
            let originalImageId = existingImage.originalImageId;
            if (existingImage.imageType === "original") {
                originalImageId = existingImage.id;
            }
            return getSiblingImages(originalImageId)
                .mergeMap(({response: {object: siblingImages}}) => Observable.of(
                    ResourceActions.receivedAllResourcesAction(ResourceApi.Types.Image, siblingImages),
                    receivedSiblingImages(siblingImages)
                ));
        } else {
            // We have the image already and there are no siblings to fetch
            return Observable.empty();
        }
    });

// when editing a RetailerItem's image, ensure that it gets passed through the Images
// reducer since the Image Info modal is managed in Redux.
const receivedRetailerItemImageEpic = (action$, store) => action$.ofType(ResourceActions.Types.RECEIVED_RESOURCE)
    .filter(action => store.getState().rewards.writingRetailerItem)
    .map(action => pickedOriginalAction(action.resource));

const saveImageDataEpic = (action$, store) => action$.ofType(Types.SAVE_IMAGE_DATA)
    .mergeMap(action => {
        const state = store.getState();

        // We needed the id sent to know which image to modify
        // but we don't want to change it so
        // store id in variable
        // and delete id field
        const id = action.image.id || state.images.selectedImageId;
        let data = clone(action.image);
        delete data.id;
        return ResourceApi.saveChangelist(ResourceApi.Types.Image, id, data, [{type: ResourceApi.Types.Image, id: id}])
            .mergeMap(response => Observable.of(
                savedImageDataAction(),
                ResourceActions.getResourcePageAction(ResourceApi.Types.Image, 10, getResourcePage(state.resources).page),
                endEditImageAction(),
                ResourceActions.saveResourceChangelistSuccess(ResourceApi.Types.Image, id, data)
            ))
            .catch(err => Observable.of({type: "ERROR", err: err}));
    });

const removeImageFromCurrentBreakEpic = (action$, store) => action$.ofType(Types.REMOVE_IMAGE_FROM_CURRENT_BREAK)
    .mergeMap(action => {
        let currentEditBreakId = isNoU(store.getState().editBreak.break) ? null : store.getState().editBreak.break["MASTER"];
        if (isNoU(currentEditBreakId)) {
            return Observable.empty();
        }

        // Remove current breaks resourceId from break
        let usedBy = action.image.usedBy.filter((item) => item.type !== ResourceApi.Types.Break && item.id !== currentEditBreakId);

        // Create action with action method to maintain consistency
        let saveAction = ResourceActions.saveResourceFieldAction(action.image.resourceId, action.image.resourceId, [], ["usedBy"], "usedBy", usedBy, [], [], []);

        // Need to use api directly in order to dispatch custom action afterward in addition to saved field action
        return ResourceApi.saveField(ResourceApi.Types.Image, action.image.id, "usedBy", usedBy, [action.image.resourceId])
            .mergeMap(({response: {object}}) => Observable.of(
                removedImageFromCurrentBreakAction(),
                ResourceActions.resourceFieldSavedAction(saveAction)
            ));
    });

const deleteImageEpic = (action$) => action$.ofType(Types.DELETE_IMAGE)
    .mergeMap(action => ResourceApi.deleteConfirmation(ResourceApi.Types.Image, action.imageId, [], {})
        .map((response) => deletedImageAction()));

export default [
    uploadOriginalEpic,
    uploadOriginalAndCreateImageEpic,
    createImageEpic,
    startEditImageEpic,
    receivedRetailerItemImageEpic,
    saveImageDataEpic,
    pickOriginalEpic,
    removeImageFromCurrentBreakEpic,
    deleteImageEpic,
    generateImagesEpic,
    nativeUploadImageEpic
];



// WEBPACK FOOTER //
// ./public/pages/Images/epics.js